<template>
  <div class="analytics">
    <!-- Performance Chart -->
    <v-card flat class="chart-card">
      <div class="chart-header">
        <div>
          <h3 class="chart-title">Performance CA & Sessions</h3>
          <span class="chart-subtitle">vs Objectifs — {{ view === 'month' ? 'Vue quotidienne' : 'Vue mensuelle' }}</span>
        </div>
        <div class="chart-legend">
          <button
            type="button"
            class="legend-item"
            :class="{ 'legend-item--off': !perfLegendVisibility.caReal }"
            @click="togglePerfLegend('caReal')"
          >
            <span class="legend-dot" style="background: #EA580C;"></span>
            CA Réel
          </button>
          <button
            type="button"
            class="legend-item"
            :class="{ 'legend-item--off': !perfLegendVisibility.caObj }"
            @click="togglePerfLegend('caObj')"
          >
            <span class="legend-line" style="border-color: #DC2626;"></span>
            Obj CA
          </button>
          <button
            type="button"
            class="legend-item"
            :class="{ 'legend-item--off': !perfLegendVisibility.sessions }"
            @click="togglePerfLegend('sessions')"
          >
            <span class="legend-dot" style="background: #0284C7;"></span>
            Sessions
          </button>
          <button
            type="button"
            class="legend-item"
            :class="{ 'legend-item--off': !perfLegendVisibility.sessObj }"
            @click="togglePerfLegend('sessObj')"
          >
            <span class="legend-line" style="border-color: #64748B;"></span>
            Obj Sessions
          </button>
        </div>
      </div>
      <div class="chart-wrapper">
        <vue-apex-charts
          v-if="perfChartOptions && perfChartSeries.length > 0"
          ref="perfChart"
          type="line"
          height="360"
          :options="perfChartOptions"
          :series="perfChartSeries"
        />
        <div v-else class="chart-placeholder">
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            :color="RMR_INDIGO"
            size="32"
            width="3"
          />
          <span>{{ isLoading ? 'Chargement du graphique...' : 'Aucune donnée disponible' }}</span>
        </div>
      </div>
    </v-card>

    <!-- RPM Evolution Chart -->
    <v-card flat class="chart-card">
      <div class="chart-header">
        <div>
          <h3 class="chart-title">Évolution RPM</h3>
          <span class="chart-subtitle">vs Objectif RPM</span>
        </div>
      </div>
      <div class="chart-wrapper">
        <vue-apex-charts
          v-if="rpmChartOptions && rpmChartSeries.length > 0"
          type="area"
          height="260"
          :options="rpmChartOptions"
          :series="rpmChartSeries"
        />
        <div v-else class="chart-placeholder">
          <v-progress-circular
            v-if="isLoading"
            indeterminate
            color="#7C3AED"
            size="32"
            width="3"
          />
          <span>{{ isLoading ? 'Chargement du graphique...' : 'Aucune donnée disponible' }}</span>
        </div>
      </div>
    </v-card>

    <!-- Daily Reporting Table -->
    <v-card flat class="table-card">
      <div class="table-header">
        <div>
          <h3 class="table-title">Reporting Quotidien</h3>
          <span class="table-subtitle">Données détaillées</span>
        </div>
        <v-chip small outlined :color="RMR_INDIGO" class="table-badge">
          {{ dailyTotal }} jours
        </v-chip>
      </div>

      <v-data-table
        :headers="tableHeaders"
        :items="dailyData"
        :items-per-page="tableOptions.itemsPerPage"
        :options.sync="tableOptions"
        :server-items-length="dailyTotal"
        :loading="isLoading"
        class="analytics-table"
        :footer-props="{
          'items-per-page-text': 'Lignes par page',
          'items-per-page-options': [5, 10, 15, 25],
        }"
      >
        <template v-slot:item.date="{ item }">
          <span class="td-date">{{ item.date }}</span>
        </template>
        <template v-slot:item.sessions="{ item }">
          <span class="td-sessions">{{ formatNumber(item.sessions) }}</span>
        </template>
        <template v-slot:item.sessObj="{ item }">
          <span class="td-obj">{{ formatNumber(item.sessObj) }}</span>
        </template>
        <template v-slot:item.ca="{ item }">
          <span class="td-ca">{{ formatCurrency(item.ca) }}</span>
        </template>
        <template v-slot:item.caObj="{ item }">
          <span class="td-obj">{{ formatCurrency(item.caObj) }}</span>
        </template>
        <template v-slot:item.rpm="{ item }">
          <span class="td-rpm">{{ item.rpm.toFixed(2) }}€</span>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { computed, reactive, ref, watch } from '@vue/composition-api'
import VueApexCharts from 'vue-apexcharts'
import { buildCacheOptions, buildScopeParams } from '@/store/dashboard-v2'
import useProxy from '@/hooks/useProxy'
import useAxios from '@/hooks/useAxios'
import { RMR_INDIGO } from '@/styles/tokens'

const COMPONENT_NAME = 'DashboardV2Analytics'

const TABLE_HEADERS = [
  { text: 'Date',     value: 'date',    sortable: true },
  { text: 'Sessions', value: 'sessions', align: 'end', sortable: true },
  { text: 'Obj Sess', value: 'sessObj',  align: 'end', sortable: true },
  { text: 'CA',       value: 'ca',       align: 'end', sortable: true },
  { text: 'Obj CA',   value: 'caObj',    align: 'end', sortable: true },
  { text: 'RPM',      value: 'rpm',      align: 'end', sortable: true },
]

const SORT_FIELD_MAP = {
  date: 'date', sessions: 'sessions', sessObj: 'obj_sessions',
  ca: 'ca_real', caObj: 'obj_ca', rpm: 'rpm',
}

const REVERSE_SORT_FIELD_MAP = Object.fromEntries(
  Object.entries(SORT_FIELD_MAP).map(([uiKey, apiKey]) => [apiKey, uiKey])
)

const SERIES_NAME_MAP = {
  caReal: 'CA Réel', caObj: 'Obj CA', sessions: 'Sessions', sessObj: 'Obj Sessions',
}

export default {
  name: COMPONENT_NAME,
  components: { VueApexCharts },
  props: {
    view: { type: String, default: 'month' },
  },
  setup() {
    const { store, refs } = useProxy()
    const { axiosGet, isLoading } = useAxios()

    // => CONST
    const perfChartOptions = ref(null)
    const perfChartSeries = ref([])
    const perfLegendVisibility = reactive({ caReal: true, caObj: true, sessions: true, sessObj: true })
    const rpmChartOptions = ref(null)
    const rpmChartSeries = ref([])
    const tableOptions = ref({
      page: 1,
      itemsPerPage: 10,
      sortBy: ['date'],
      sortDesc: [false],
    })

    // => METHODS
    const formatNumber = (num) => (num || 0).toLocaleString('fr-FR')
    const formatCurrency = (num) =>
      (num || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '€'

    const buildPerfChart = (dates, caValues, caObjValues, sessValues, sessObjValues) => {
      perfLegendVisibility.caReal = true
      perfLegendVisibility.caObj = true
      perfLegendVisibility.sessions = true
      perfLegendVisibility.sessObj = true

      perfChartSeries.value = [
        { name: 'CA Réel',      data: caValues,      type: 'line' },
        { name: 'Obj CA',       data: caObjValues,   type: 'line' },
        { name: 'Sessions',     data: sessValues,    type: 'line' },
        { name: 'Obj Sessions', data: sessObjValues, type: 'line' },
      ]
      perfChartOptions.value = {
        chart: { type: 'line', toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif', zoom: { enabled: false } },
        colors: ['#EA580C', '#DC2626', '#0284C7', '#64748B'],
        stroke: { width: [3, 2, 3, 2], curve: 'smooth', dashArray: [0, 5, 0, 5] },
        markers: { size: [4, 0, 4, 0], strokeWidth: 2, strokeColors: '#fff', hover: { size: 6 } },
        fill: { type: ['solid', 'solid', 'solid', 'solid'], opacity: [1, 1, 1, 1] },
        xaxis: {
          categories: dates,
          labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, rotate: -45, rotateAlways: dates.length > 12 },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: [
          {
            seriesName: 'CA Réel',
            title: { text: 'CA (€)', style: { color: '#EA580C', fontWeight: 700, fontSize: '11px' } },
            labels: { style: { colors: '#EA580C', fontSize: '10px', fontWeight: 600 }, formatter: (val) => val >= 1000 ? (val / 1000).toFixed(1) + 'k€' : val + '€' },
          },
          { show: false, seriesName: 'CA Réel' },
          {
            seriesName: 'Sessions',
            opposite: true,
            title: { text: 'Sessions', style: { color: '#0284C7', fontWeight: 700, fontSize: '11px' } },
            labels: { style: { colors: '#0284C7', fontSize: '10px', fontWeight: 600 }, formatter: (val) => (val / 1000).toFixed(0) + 'k' },
          },
          { show: false, seriesName: 'Sessions' },
        ],
        grid: { borderColor: '#F1F5F9', strokeDashArray: 4, xaxis: { lines: { show: false } } },
        legend: { show: false },
        tooltip: {
          shared: true, intersect: false,
          y: {
            formatter: (val, { seriesIndex }) => {
              if (val == null || Number.isNaN(Number(val))) return '--'
              const n = Number(val)
              return seriesIndex <= 1 ? `${n.toLocaleString('fr-FR')}€` : n.toLocaleString('fr-FR')
            },
          },
        },
      }
    }

    const buildRpmChart = (dates, rpmValues) => {
      rpmChartSeries.value = [{ name: 'RPM', data: rpmValues }]
      rpmChartOptions.value = {
        chart: { type: 'area', toolbar: { show: false }, fontFamily: 'Inter, system-ui, sans-serif', zoom: { enabled: false } },
        colors: ['#7C3AED'],
        stroke: { width: 3, curve: 'smooth' },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.05, stops: [0, 100] } },
        markers: { size: 3, strokeWidth: 2, strokeColors: '#fff', hover: { size: 5 } },
        xaxis: {
          categories: dates,
          labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 } },
          axisBorder: { show: false }, axisTicks: { show: false },
        },
        yaxis: { labels: { style: { colors: '#94A3B8', fontSize: '10px', fontWeight: 600 }, formatter: (val) => val.toFixed(2) + '€' } },
        grid: { borderColor: '#F1F5F9', strokeDashArray: 4, xaxis: { lines: { show: false } } },
        annotations: {
          yaxis: [{
            y: 12, borderColor: '#DC2626', strokeDashArray: 4,
            label: {
              text: 'Obj RPM: 12.00€', position: 'left',
              style: { color: '#DC2626', fontSize: '10px', fontWeight: 700, background: '#FEE2E2', padding: { left: 8, right: 8, top: 4, bottom: 4 } },
            },
          }],
        },
        tooltip: { y: { formatter: (val) => val.toFixed(2) + '€' } },
      }
    }

    const togglePerfLegend = (key) => {
      const nextVisible = !perfLegendVisibility[key]
      perfLegendVisibility[key] = nextVisible
      const seriesName = SERIES_NAME_MAP[key]
      if (!seriesName || !refs.perfChart) return
      if (nextVisible) {
        refs.perfChart.showSeries(seriesName)
      } else {
        refs.perfChart.hideSeries(seriesName)
      }
    }

    const fetchTablePage = async () => {
      const opts = tableOptions.value
      const sortKey = opts.sortBy?.[0] || 'date'
      const sortBy = SORT_FIELD_MAP[sortKey] || 'date'
      const sortDesc = Boolean(opts.sortDesc?.[0])
      const page = opts.page || 1
      const limit = opts.itemsPerPage || 10

      const existing = store.getters['dashboardV2/getAnalyticsData']
      if (
        existing &&
        existing.page === page &&
        existing.limit === limit &&
        existing.sortBy === sortBy &&
        existing.sortDesc === sortDesc
      ) {
        return
      }

      const requestGeneration = store.getters['dashboardV2/getRefreshGeneration']
      const state = store.state.dashboardV2
      const params = {
        date_start: state.dateFrom,
        date_end: state.dateTo,
        ...buildScopeParams(state, true),
        limit,
        page,
        sort_by: sortBy,
        sort_desc: sortDesc,
      }
      try {
        const { data } = await axiosGet('/dashboard/daily-reporting', params, buildCacheOptions(state.dateTo))
        if (requestGeneration !== store.getters['dashboardV2/getRefreshGeneration']) return

        const freshAnalytics = store.getters['dashboardV2/getAnalyticsData']
        const dailyData = (data?.items || []).map((item) => ({
          date: item.date, sessions: Number(item.sessions || 0), sessObj: Number(item.obj_sessions || 0),
          ca: Number(item.ca_real || 0), caObj: Number(item.obj_ca || 0), rpm: Number(item.rpm || 0),
        }))
        store.commit('dashboardV2/setAnalyticsData', {
          ...(freshAnalytics || {}),
          dailyData, total: Number(data?.total || 0),
          page, limit, sortBy, sortDesc,
        })
      } catch (error) {
        console.error('Error fetching analytics page:', error)
      }
    }

    // => COMPUTED
    const analyticsData = computed(() => store.getters['dashboardV2/getAnalyticsData'])
    const dailyData = computed(() => analyticsData.value?.dailyData || [])
    const dailyTotal = computed(() => analyticsData.value?.total || 0)

    // => WATCH
    watch(
      () => analyticsData.value?.dates,
      (dates) => {
        const data = analyticsData.value
        if (dates?.length && data) {
          buildPerfChart(data.dates, data.caValues, data.caObjValues, data.sessValues, data.sessObjValues)
          buildRpmChart(data.dates, data.rpmValues)
        } else {
          perfChartOptions.value = null
          perfChartSeries.value = []
          rpmChartOptions.value = null
          rpmChartSeries.value = []
        }
      },
      { immediate: true }
    )

    watch(
      analyticsData,
      (data) => {
        if (!data) return
        const uiSortKey = REVERSE_SORT_FIELD_MAP[data.sortBy] || 'date'
        const opts = tableOptions.value
        if (
          opts.page !== data.page ||
          opts.itemsPerPage !== data.limit ||
          (opts.sortBy?.[0] || 'date') !== uiSortKey ||
          Boolean(opts.sortDesc?.[0]) !== Boolean(data.sortDesc)
        ) {
          tableOptions.value = {
            ...opts,
            page: data.page || 1,
            itemsPerPage: data.limit || 10,
            sortBy: [uiSortKey],
            sortDesc: [Boolean(data.sortDesc)],
          }
        }
      }
    )

    watch(tableOptions, () => { fetchTablePage() }, { deep: true })

    // => DATA
    return {
      perfChartOptions,
      perfChartSeries,
      perfLegendVisibility,
      rpmChartOptions,
      rpmChartSeries,
      tableOptions,
      tableHeaders: TABLE_HEADERS,
      isLoading,
      dailyData,
      dailyTotal,
      formatNumber,
      formatCurrency,
      togglePerfLegend,
      RMR_INDIGO,
    }
  },
}
</script>

<style lang="scss" scoped>
.analytics {
  display: flex;
  flex-direction: column;
  gap: $rmr-space-xl;
}

.chart-card {
  border-radius: $rmr-radius-2xl !important;
  padding: $rmr-space-xl;
  box-shadow: $rmr-shadow-card !important;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: $rmr-space-sm;
}

.chart-title {
  font-size: $rmr-fs-title;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-indigo-dark;
  letter-spacing: 0.5px;
  margin: 0;
}

.chart-subtitle {
  display: block;
  font-size: $rmr-fs-base;
  color: $rmr-text-subtle;
  font-weight: 500;
  margin-top: 4px;
}

.chart-legend {
  display: flex;
  gap: $rmr-space-md;
  flex-wrap: wrap;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: $rmr-space-xs;
  font-size: $rmr-fs-base;
  color: $rmr-text-muted;
  font-weight: 600;
  cursor: pointer;
}

.legend-item--off {
  opacity: 0.35;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.legend-line {
  width: 16px;
  height: 0;
  border-top: 2px dashed;
}

.chart-wrapper {
  min-height: 200px;
}

.chart-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $rmr-space-sm;
  min-height: 300px;
  color: $rmr-text-subtle;
  font-size: $rmr-fs-md;
  font-weight: 500;
}

.table-card {
  border-radius: $rmr-radius-2xl !important;
  padding: $rmr-space-xl;
  box-shadow: $rmr-shadow-card !important;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $rmr-space-md;
  padding-bottom: $rmr-space-sm;
  border-bottom: 1px solid $rmr-border;
}

.table-title {
  font-size: $rmr-fs-title;
  font-weight: 700;
  text-transform: uppercase;
  color: $rmr-indigo-dark;
  letter-spacing: 0.5px;
  margin: 0;
}

.table-subtitle {
  display: block;
  font-size: $rmr-fs-base;
  color: $rmr-text-subtle;
  font-weight: 500;
  margin-top: 2px;
}

.table-badge {
  font-weight: 700 !important;
  font-size: $rmr-fs-sm !important;
}

.analytics-table {
  border-radius: $rmr-radius-lg;
  overflow: hidden;

  ::v-deep .v-data-table-header th {
    font-size: $rmr-fs-sm !important;
    font-weight: 700 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.8px !important;
    color: $rmr-text-subtle !important;
    background: $rmr-surface-2 !important;
    border-bottom: 1px solid $rmr-border !important;
    padding: $rmr-space-sm $rmr-space-md !important;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    transition: background-color 0.15s ease;

    &:hover { background: $rmr-surface-1 !important; }
  }

  ::v-deep tbody td {
    padding: 10px $rmr-space-md !important;
    font-size: $rmr-fs-body !important;
    border-bottom: 1px solid $rmr-surface-2 !important;
  }
}

.td-date     { font-weight: 600; color: $rmr-text-medium; }
.td-sessions { font-weight: 700; color: $rmr-sessions; }
.td-ca       { font-weight: 700; color: $rmr-ca; }
.td-rpm      { font-weight: 700; color: $rmr-rpm; }
.td-obj      { color: $rmr-text-subtle; font-style: italic; font-weight: 500; }

@media (max-width: 960px) {
  .chart-header { flex-direction: column; }
  .chart-card   { padding: $rmr-space-md; }
  .table-card   { padding: $rmr-space-md; }
}
</style>
